import { useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { captureAnalyticsEvent } from '../../analytics/posthog';
import { updateChatTopicOnServer } from '../../utils/chatApi';

export const useChatOperations = (chats) => {
  const shareChat = useCallback(async (chatId) => {
    if (!chatId) return;
    
    try {
      const chat = chats.find(c => c.id === chatId);
      if (!chat) return;
      
      const shareUrl = `${window.location.origin}/chat/${chatId}`;
      
      if (navigator.share) {
        await navigator.share({
          title: chat.topic || 'Shared Chat',
          text: `Check out this conversation: ${chat.topic || 'Untitled Chat'}`,
          url: shareUrl,
        });
        captureAnalyticsEvent('chat_shared', {
          chat_id: chatId,
          method: 'native_share',
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Chat link copied to clipboard!');
        captureAnalyticsEvent('chat_shared', {
          chat_id: chatId,
          method: 'clipboard',
        });
      }
    } catch (error) {
      console.error('Error sharing chat:', error);
    }
  }, [chats]);

  const archiveChat = useCallback(async (chatId) => {
    if (!chatId) return;
    
    try {
      const chatRef = doc(firestore, 'chats', chatId);
      await updateDoc(chatRef, { archived: true });
      captureAnalyticsEvent('chat_archived', { chat_id: chatId });
    } catch (error) {
      console.error('Error archiving chat:', error);
    }
  }, []);

  const unarchiveChat = useCallback(async (chatId) => {
    if (!chatId) return;
    
    try {
      const chatRef = doc(firestore, 'chats', chatId);
      await updateDoc(chatRef, { archived: false });
      captureAnalyticsEvent('chat_unarchived', { chat_id: chatId });
    } catch (error) {
      console.error('Error unarchiving chat:', error);
    }
  }, []);

  const renameChat = useCallback(async (chatId, newTopic) => {
    if (!chatId || !newTopic.trim()) return;

    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;

    if (newTopic.trim() !== chat.topic) {
      try {
        await updateChatTopicOnServer(chatId, newTopic.trim());
        captureAnalyticsEvent('chat_renamed', {
          chat_id: chatId,
          new_topic: newTopic.trim(),
        });
      } catch (error) {
        console.error('Error renaming chat:', error);
      }
    }
  }, [chats]);

  return {
    shareChat,
    archiveChat,
    unarchiveChat,
    renameChat
  };
};
